import {
  Button,
  createStyles,
  Menu,
  ScrollArea,
  Table,
  Flex,
  TextInput,
  Badge,
  Text,
  Box,
} from "@mantine/core";
import TableLoader from "../TableLoader";
import { BsThreeDotsVertical } from "react-icons/bs";
import { useEffect, useState } from "react";
import { formatDate } from "../../utils";
import { GoDotFill } from "react-icons/go";
import { useSelector } from "react-redux";
import {
  addSkypointProjectTaskStatus,
  deleteSkypointProjectTask,
  getAllSkypointProjectTasks,
} from "../../services/skypointProject";
import CustomPagination from "../Pagination/CustomPagination";
import useDebounce from "../../utils/useDebounce";
import { ReportIconSvg } from "../Svgs/ReportIconSvg";
import NewTaskModal from "../ProjectModal";
import { useDisclosure } from "@mantine/hooks";
import swal from "sweetalert";
import RecordNotFound from "../RecordNotFound";
import { isStaff } from "../../http";

const useStyles = createStyles((theme) => ({
  header: {
    position: "sticky",
    top: 0,
    zIndex: 1,
    transition: "box-shadow 150ms ease",
    "& th": {
      backgroundColor: "#fff !important",
      color: "#000 !important",
    },
  },
  scrollBg: {
    backgroundColor: "#fff",
    padding: "12px 8px",
    borderRadius: "16px",
  },
  tbl: {
    borderRadius: "8px !important",
    borderCollapse: "separate",
    overflow: "hidden",
  },
  scrolled: {
    boxShadow: theme.shadows.sm,
  },
  tbody: {
    "& tr:nth-of-type(even) td": {
      background: "#fff",
    },
    "& tr:nth-of-type(odd) td": {
      background: "#F9FAFB",
    },
    "& tr td": {
      padding: "14px 12px !important",
      fontSize: "14px !important",
      fontWeight: 400,
      fontFamily: "Inter",
      color: "#000",
    },
    cursor: "pointer",
  },
  topTabs: {
    borderBottom: "1px solid #E5E7EB",
    paddingBottom: 8,
    marginBottom: 12,
  },
  tabButton: {
    background: "transparent",
    color: "#000",
    fontWeight: 500,
    padding: "6px 14px",
    borderRadius: 8,
    "&:hover": {
      background: "#F3F4F6",
    },
  },
  tabActive: {
    background: "#000",
    color: "#fff",
  },
  status: {
    padding: "0px 8px 0px 6px",
    fontSize: 12,
    fontWeight: 500,
    fontFamily: "Inter",
    color: "#344054",
    height: 22,
    borderRadius: 16,
    backgroundColor: "#F2F4F7",
    textTransform: "uppercase",
    alignItems: "center",
    "& .mantine-Badge-leftSection": {
      display: "flex",
      alignItems: "center",
      paddingBottom: 1,
      marginRight: 4,
      "& svg": {
        color: "#667085",
      },
    },
  },

  greenStatus: {
    color: "#027A48",
    backgroundColor: "#ECFDF3",
    "& .mantine-Badge-leftSection": {
      "& svg": {
        color: "#12B76A",
      },
    },
  },

  blueStatus: {
    color: "#193CB8",
    backgroundColor: "#DBEAFE",
    "& .mantine-Badge-leftSection": {
      "& svg": {
        color: "#193CB8",
      },
    },
  },

  orangeStatus: {
    color: "#9F2D00",
    backgroundColor: "#FFEDD4",
    "& .mantine-Badge-leftSection": {
      "& svg": {
        color: "#9F2D00",
      },
    },
  },

  purpleStatus: {
    color: "#6E11B0",
    backgroundColor: "#F3E8FF",
    "& .mantine-Badge-leftSection": {
      "& svg": {
        color: "#6E11B0",
      },
    },
  },
  emptyReportsBox: {
    minHeight: 442,
    padding: 41,
    margin: "auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderRadius: 20,
    border: "1px solid #E5E5E5",
    background: "#FFF",
    maxWidth: 761,
    width: "100%",
  },
  iconBox: {
    height: 158,
    width: 158,
    borderRadius: "50%",
    borderTop: "1px solid #D4D4D4",
    background:
      "linear-gradient(180deg, #F1F1F1 0%, rgba(255, 255, 255, 0.00) 81.19%)",
    "& svg": {
      height: 70,
      width: 70,
    },
  },

  generateBtn: {
    marginTop: 44,
    padding: "12px 20px",
    borderRadius: "8px",
    border: "1px solid #FFAF4A",
    backgroundColor: "#FFAF4A",
    height: 44,
    boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.05)",
    fontSize: 16,
    fontWeight: 600,
    fontFamily: "Inter",
    color: "#262626",
    ":hover": {
      backgroundColor: "#FFAF4A",
      opacity: 0.8,
    },
  },
  notFoundText: {
    color: "#A8A8A8",
    fontFamily: "Inter",
    fontSize: "22px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "normal",
  },
  notFoundTextContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-evenly",
    height: "75vh",
    gap: "8rem",
  },
  tableContainer: {
    display: "flex",
    flexDirection: "column",
    minHeight: "75vh", 
    justifyContent: "space-between",
  }
}));

const SkypointProjectTable = () => {
  const [scrolled, setScrolled] = useState(false);
  const { classes, cx } = useStyles();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 2000);
  const [selectedTask, setSelectedTask] = useState(null);
  const [mode, setMode] = useState("view");
  const [taskModalOpened, { open: taskModalOpen, close: taskModalClose }] =
    useDisclosure(false);
  const { profile } = useSelector((state) => state.profile);

  const {
    skypointProject,
    skypointProjectCount,
    skypointProjectTotalCount,
    skypointProjectLoading,
  } = useSelector((state) => state?.skypointProject);

  const getClassname = (status) => {
    switch (status) {
      case "done":
        return classes.greenStatus;
      case "in progress":
        return classes.blueStatus;
      case "in review":
        return classes.purpleStatus;
      case "to do":
        return "";
      default:
        return classes.orangeStatus;
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    getAllSkypointProjectTasks(currentPage, debouncedSearch);
  }, [currentPage, debouncedSearch]);


  const handleTaskEditClick = (data) => {
    setSelectedTask(data);
    setMode("edit");
    taskModalOpen();
  };

  const handleTaskViewClick = (data) => {
    setSelectedTask(data);
    setMode("view");
    taskModalOpen();
  };

  const handleAddTaskClick = () => {
    setSelectedTask(null);
    setMode("add");
    taskModalOpen();
  };

  const handleTaskDeleteClick = async (data) => {
    const willDelete = await swal({
      title: `Are you sure?`,
      text: "This action cannot be undone.",
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Delete"],
    });
    if (willDelete) {
      deleteSkypointProjectTask(data?.id);
    }
  };

  return (
    <>
      {skypointProjectTotalCount > 0 ||
      skypointProjectLoading ||
      skypointProject?.length > 0 ? (
        <div className={classes.tableContainer}>
          <div>
            <ScrollArea
              onScrollPositionChange={({ y }) => setScrolled(y !== 0)}
              className={cx(classes.scrollBg)}
            >
              {/* === ALL TASK BUTTON + SEARCH BAR === */}
              <Flex align="center" gap={12} mx={10} mb={14}>
                {/* ALL TASK BUTTON WITH COUNT BADGE */}
                <Button
                  bg="#000"
                  color="#fff"
                  radius="md"
                  px={20}
                  py={6}
                  styles={{
                    root: {
                      backgroundColor: "#000",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      "&:hover": {
                        backgroundColor: "#111",
                      },
                    },
                  }}
                >
                  All Tasks
                  <span
                    style={{
                      background: "#fff",
                      color: "#000",
                      borderRadius: "50%",
                      width: 22,
                      height: 22,
                      fontSize: 12,
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginLeft: 8, // Add left margin instead of right margin
                    }}
                  >
                    {skypointProjectTotalCount}
                  </span>
                </Button>

                {/* SEARCH BAR TAKES REMAINING SPACE */}
                <TextInput
                  placeholder="Search staff name"
                  style={{ flex: 1 }}
                  radius="md"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </Flex>
              <Table
                withBorder
                stickyHeader
                sx={{ fontFamily: "Inter" }}
                className={classes.tbl}
                fontSize="sm"
                verticalSpacing="md"
              >
                <thead
                  className={cx(classes.header, {
                    [classes.scrolled]: scrolled,
                  })}
                >
                  <tr style={{ height: "60px" }}>
                    <th style={{ minWidth: "180px" }}>Title</th>
                    <th style={{ minWidth: "140px" }}>Date</th>
                    <th style={{ minWidth: "160px" }}>Assigned To</th>
                    <th style={{ minWidth: "140px" }}>Status</th>
                    <th style={{ minWidth: "120px" }}>Actions</th>
                  </tr>
                </thead>

                <tbody className={classes.tbody}>
                  {skypointProjectLoading ? (
                    <tr>
                      <td colSpan={1000}>
                        <TableLoader />
                      </td>
                    </tr>
                  ) : skypointProjectTotalCount > 0 &&
                    skypointProjectCount === 0 &&
                    skypointProject?.length === 0 ? (
                    <tr>
                      <td colSpan={1000}>
                        <Box className={classes.notFoundTextContainer}>
                          <RecordNotFound />
                          <Text className={classes.notFoundText}>
                            You don&apos;t have any record, click on “New Task"
                          </Text>
                        </Box>
                      </td>
                    </tr>
                  ) : (
                    skypointProject?.map((v) => (
                      <tr key={v.id} onClick={() => handleTaskViewClick(v)}>
                        <td>{v?.title || "-"}</td>
                        <td>{v?.date ? formatDate(v.date) : ""}</td>
                        <td>{v?.assignedTo?.name || "Unassigned"}</td>
                        <td>
                          <Badge
                            leftSection={<GoDotFill />}
                            className={cx(
                              classes.status,
                              getClassname(v?.status?.status)
                            )}
                          >
                            {v?.status?.status}
                          </Badge>
                        </td>

                        <td onClick={(e) => e.stopPropagation()}>
                          <Menu shadow="md" width={100} withinPortal>
                            <Menu.Target>
                              <Button variant="unstyled">
                                <BsThreeDotsVertical />
                              </Button>
                            </Menu.Target>

                            <Menu.Dropdown>
                              {!isStaff(profile?.user?.role) && (
                                <Menu.Item
                                  onClick={() => handleTaskEditClick(v)}
                                >
                                  Edit
                                </Menu.Item>
                              )}
                              <Menu.Item onClick={() => handleTaskViewClick(v)}>
                                View
                              </Menu.Item>
                              {!isStaff(profile?.user?.role) && (
                                <Menu.Item
                                  onClick={() => handleTaskDeleteClick(v)}
                                >
                                  Delete
                                </Menu.Item>
                              )}
                            </Menu.Dropdown>
                          </Menu>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </ScrollArea>
          </div>
          <CustomPagination
            data={skypointProjectCount}
            itemsPerPage={10}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
          />
        </div>
      ) : (
        <Box className={classes.emptyReportsBox}>
          <Flex align="center" justify="center" className={classes.iconBox}>
            <ReportIconSvg color="#262626" />
          </Flex>

          <Text fz={20} fw={600} ff="Inter" color="#171717">
            Manage team projects and visualize tasks in boards
          </Text>

          <Text mt={10} fz={16} fw={400} ff="Inter" color="#737373">
            No data available. Kindly add a task to get started
          </Text>

          {!isStaff(profile?.user?.role) && (
            <Button
              className={classes.generateBtn}
              onClick={handleAddTaskClick}
            >
              Add Task
            </Button>
          )}
        </Box>
      )}
      <NewTaskModal
        opened={taskModalOpened}
        onClose={() => {
          setSelectedTask(null);
          setMode("add");
          taskModalClose();
        }}
        mode={mode}
        selectedTask={selectedTask}
      />
    </>
  );
};

export default SkypointProjectTable;
